import { decodeToken } from "react-jwt";
import type { ReadonlyDeep } from "type-fest";
import Cookies from "universal-cookie";

import type { DataGroupEntity } from "src/app/features/DataGroup/types/DataGroupEntity";
import type { CredentialsDto } from "../../types/be/generated";
import { isDevelopment } from "../../utils/appVersion";
import type { AuthToken, RefreshToken } from "./types";

export enum StorageEvents {
    DATA_GROUP_CHANGED = "DataGroupChanged",
}

type JWToken = {
    oid: string;
    exp: number;
    given_name: string;
    unique_name: string;
    iat: number;
    id: number;
    jti: number;
    sub: string;
    tokenType: string;
};

const cookies = new Cookies();

const APA_ACCESS_TOKEN = "jwtApaAccessToken";
const APA_ID_TOKEN = "jwtApaIdToken";
const IDM_REFRESH_TOKEN = "idmRefreshToken";
const SELECTED_DATA_GROUP = "selectedDataGroup";
const USER_RIGHTS = "userRights";
const LAST_ACTIVITY = "apaLastActivity";

// eslint-disable-next-line no-underscore-dangle
const isMSEntraLogin = window.__RUNTIME_CONFIG__?.IDENTITY_SERVICE === "entra";

const cookieOptions = () => (isDevelopment() ? {} : { secure: true, sameSite: "strict" as "strict", path: "/" });

// These tokens are only ever read client-side to build the `Authorization: Bearer` header (see
// baseApi.prepareHeaders); nothing reads them from the Cookie header server-side. For MS Entra the
// JWTs are large (id token carries all `roles` claims), so keeping them in cookies meant every
// request to the app host shipped several KB of cookies and overflowed the server's header limit
// (HTTP 400 "Request Header Or Cookie Too Large"). For Entra we store them in localStorage instead
// so they never touch request headers; the IDM flow keeps its (smaller) cookie tokens unchanged.
export const setAccessToken = (accessToken: AuthToken | null) => {
    if (!accessToken) {
        deleteCookieAccessToken();
        return;
    }
    if (isMSEntraLogin) {
        window.localStorage.setItem(APA_ACCESS_TOKEN, accessToken);
    } else {
        cookies.set(APA_ACCESS_TOKEN, accessToken, cookieOptions());
    }
};

export const getAccessToken = (): AuthToken | null => {
    return isMSEntraLogin ? window.localStorage.getItem(APA_ACCESS_TOKEN) : cookies.get(APA_ACCESS_TOKEN);
};

export const setIdToken = (idToken: AuthToken | null) => {
    if (!idToken) {
        deleteCookieIdToken();
        return;
    }
    if (isMSEntraLogin) {
        window.localStorage.setItem(APA_ID_TOKEN, idToken);
    } else {
        cookies.set(APA_ID_TOKEN, idToken, cookieOptions());
    }
};

export const getIdToken = (): AuthToken | null => {
    return isMSEntraLogin ? window.localStorage.getItem(APA_ID_TOKEN) : cookies.get(APA_ID_TOKEN);
};

export const deleteCookieAccessToken = () => {
    if (isMSEntraLogin) {
        window.localStorage.removeItem(APA_ACCESS_TOKEN);
    } else {
        cookies.remove(APA_ACCESS_TOKEN);
    }
};

export const deleteCookieIdToken = () => {
    if (isMSEntraLogin) {
        window.localStorage.removeItem(APA_ID_TOKEN);
    } else {
        cookies.remove(APA_ID_TOKEN);
    }
};

// Real user-activity timestamp used to drive inactivity logout for the Entra flow. MSAL refreshes
// the access token silently while keeping its original `iat`, so token age can't stand in for
// activity (see isUserSessionActive).
export const markActivity = () => {
    window.localStorage.setItem(LAST_ACTIVITY, `${Date.now()}`);
};

export const getLastActivity = (): number | null => {
    const value = window.localStorage.getItem(LAST_ACTIVITY);
    return value ? Number(value) : null;
};

export const clearLastActivity = () => {
    window.localStorage.removeItem(LAST_ACTIVITY);
};

export const setUserRights = (userRights: ReadonlyDeep<CredentialsDto["userRights"]>) => {
    if (userRights) {
        const toStringUserRights: string = userRights.join(",");
        window.localStorage.setItem(USER_RIGHTS, toStringUserRights);
    }
};

export const getUserRights = (): string[] | null => {
    const userRights = window.localStorage.getItem(USER_RIGHTS);
    if (userRights) {
        return userRights.split(",");
    }
    return null;
};

export const deleteUserRights = () => {
    window.localStorage.removeItem(USER_RIGHTS);
};

export const setRefreshToken = (refreshToken: AuthToken | null) => {
    if (refreshToken) {
        window.localStorage.setItem(IDM_REFRESH_TOKEN, refreshToken);
    } else {
        deleteRefreshToken();
    }
};

export const getRefreshToken = (): RefreshToken | null => {
    return window.localStorage.getItem(IDM_REFRESH_TOKEN);
};

export const deleteRefreshToken = () => {
    window.localStorage.removeItem(IDM_REFRESH_TOKEN);
};

export const setAssignedDataGroup = (selectedDataGroup: DataGroupEntity | null) => {
    if (selectedDataGroup == null) {
        deleteAssignedDataGroup();
    } else {
        const value = JSON.stringify(selectedDataGroup);
        window.localStorage.setItem(SELECTED_DATA_GROUP, value);
    }

    window.dispatchEvent(new Event(StorageEvents.DATA_GROUP_CHANGED));
};

export const getAssignedDataGroup = (): DataGroupEntity => {
    const selectedDataGroup = window.localStorage.getItem(SELECTED_DATA_GROUP);
    // null value is required by NoDataGroupModal
    // @ts-ignore
    return selectedDataGroup ? (JSON.parse(selectedDataGroup) as DataGroupEntity) : null;
};

export const deleteAssignedDataGroup = () => {
    window.localStorage.removeItem(SELECTED_DATA_GROUP);
};

export const getUserNameFromToken = (): string | null => {
    return getTokenInfo("given_name");
};

export const getEmailFromToken = (): string | null => {
    return getTokenInfo("unique_name");
};

export const getUserIdFromToken = (): string | number | null => {
    return isMSEntraLogin ? getTokenInfo("oid") : getTokenInfo("id");
};

const getToken = () => {
    const token = getAccessToken();
    return token ? decodeToken<JWToken>(token) : undefined;
};

// overloads for getTokenInfo
function getTokenInfo(prop: "given_name"): string | null;
function getTokenInfo(prop: "unique_name"): string | null;
function getTokenInfo(prop: "oid"): string | null;
function getTokenInfo(prop: "id"): number | null;

function getTokenInfo(prop: keyof JWToken): string | number | null {
    const token = getToken();
    if (token && prop in token) {
        return token[prop];
    }
    return null;
}
